import { EMPTY_LIST_NAME, type ClearedRow } from '../data/store.js';
import type { List, ListPatch } from '../lib/api.js';
import { el } from '../lib/dom.js';
import { openActivity } from './activity.js';
import { SEPARATOR, type MenuEntry, type MenuItem } from './menu.js';
import { actionRow, banner, confirmPanel, kindControl, messageOf, type Popup, type ScreenHost } from './ui.js';

/** List-level actions share the item menu, but own their drafts and consent. */
export function listActions(options: {
  shell: ScreenHost;
  listId: string;
  trigger: HTMLButtonElement;
  prepareEdit(): boolean;
  share(): void;
  dirtyItem(id: string): boolean;
  /** Removes exactly these rows as one Clear completed (g108), answering for each. */
  clear(ids: readonly string[]): Promise<ClearedRow>[];
  afterClear(ids: Set<string>): void;
  deleteList(): void;
  problem(message: string): void;
  settled(): void;
}) {
  const { shell, listId, trigger } = options;
  const { store } = shell;
  let popup: Popup | undefined;
  let refreshForm: ((list: List) => void) | undefined;
  let refreshQuestion: (() => void) | undefined;
  let clearing = false;
  let disposed = false;
  const completed = () => store.items(listId).filter(row => row.done).map(row => row.id).sort();
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((id, i) => id === b[i]);
  const focus = () => trigger.isConnected ? trigger : undefined;

  function clearCompleted(): void {
    if (clearing || !completed().length) return;
    let candidates = completed();
    let generation = 0;
    const dirty = () => candidates.some(id => options.dirtyItem(id));
    let warnedDirty = dirty();
    let named = store.list(listId)?.name;
    const close = () => popup?.close();
    const question = () => {
      const token = ++generation;
      const n = candidates.length;
      return {
        title: `Clear ${n} completed ${n === 1 ? 'item' : 'items'}?`,
        message: `This will delete ${n} completed ${n === 1 ? 'item' : 'items'} from “${store.list(listId)?.name ?? ''}”. Unchecked items will stay. You can't undo this in Trayflux.${warnedDirty ? ' Unsaved edits to these completed items will be discarded too.' : ''}`,
        keep: { label: 'Cancel', onChoose: close },
        act: { label: 'Clear completed', danger: true, onChoose: () => {
          if (clearing || token !== generation || disposed || !store.list(listId)) return;
          if (!same(candidates, completed()) || dirty() !== warnedDirty) { refresh(true); return; }
          clearing = true;
          refreshQuestion = undefined;
          // The confirmed set and nothing else, started synchronously: local
          // events cannot widen the consent. The store leaves a row alone that
          // is no longer in this list and ticked, and says it changed.
          const attempts = ((): Promise<ClearedRow>[] => {
            try { return options.clear(candidates); }
            catch { return candidates.map(id => Promise.resolve({ id, saved: false, changed: false })); }
          })();
          const confirmed = new Set(candidates);
          options.afterClear(confirmed);
          close();
          void Promise.all(attempts).then(results => {
            clearing = false;
            if (disposed) return;
            const n = results.filter(result => result.saved).length;
            const changed = results.filter(result => result.changed).length;
            const failed = results.length - n - changed;
            const message = failed
              ? `${n} completed ${n === 1 ? 'item' : 'items'} cleared. ${failed} ${failed === 1 ? 'removal could' : 'removals could'} not be saved on this device. Reload to check the remaining items.`
              : `${n} completed ${n === 1 ? 'item' : 'items'} cleared${changed ? `. ${changed} ${changed === 1 ? 'item changed and was' : 'items changed and were'} left alone.` : ''}`;
            shell.announce(message);
            options.settled();
            if (failed) options.problem(message);
          });
        } },
      };
    };
    function refresh(force = false): void {
      if (clearing || !popup?.isOpen()) return;
      const next = completed();
      if (!next.length) { close(); shell.announce('There are no completed items to clear.'); return; }
      if (!force && same(candidates, next) && dirty() === warnedDirty && named === store.list(listId)?.name) return;
      named = store.list(listId)?.name;
      candidates = next;
      warnedDirty = dirty();
      popup.confirm(question());
      shell.announce('Completed items changed. Please check and confirm again.');
    }
    const spec = question();
    const built = confirmPanel(spec);
    popup = shell.openPopup({ title: spec.title, body: built.node, focus: built.focus, returnFocusTo: focus,
      onClose: () => { popup = undefined; refreshQuestion = undefined; generation++; } });
    refreshQuestion = refresh;
  }

  function editList(): void {
    if (!options.prepareEdit()) return;
    const original = store.list(listId);
    if (!original) return;
    let base = original;
    let saving = false;
    let editPopup: Popup;
    const name = el('input', { attrs: { id: 'edit-list-name', maxlength: 200, value: base.name, 'aria-describedby': 'edit-list-error' } });
    const kind = kindControl(base.kind);
    kind.node.querySelector('legend')!.textContent = 'List type';
    const status = el('div', { class: 'popup-status', attrs: { id: 'edit-list-error' } });
    const saveButton = el('button', { text: 'Save', attrs: { type: 'submit' } });
    const cancelButton = el('button', { class: 'secondary', text: 'Cancel', attrs: { type: 'button' }, on: { click: () => popup?.dismiss() } });
    const changes = (): ListPatch => ({ ...(name.value.trim() !== base.name ? { name: name.value.trim() } : {}), ...(kind.value() !== base.kind ? { kind: kind.value() } : {}) });
    const error = (message: string) => { status.replaceChildren(banner(message)); shell.announce(message); };
    async function write(patch: ListPatch): Promise<void> {
      if (saving || !store.list(listId)) return;
      saving = true; saveButton.disabled = true; cancelButton.disabled = true;
      name.readOnly = true; kind.node.disabled = true; form.setAttribute('aria-busy', 'true');
      try {
        const saved = await store.patchList(listId, patch);
        if (saved) editPopup.close();
        else { editPopup.resume(); error('Could not save your list changes on this device. Your draft is still here.'); }
      } catch (e) { editPopup.resume(); error(messageOf(e)); }
      finally { saving = false; saveButton.disabled = false; cancelButton.disabled = false; name.readOnly = false; kind.node.disabled = false; form.removeAttribute('aria-busy'); }
    }
    function save(): void {
      if (saving) return;
      const patch = changes();
      if (!name.value.trim()) { name.setAttribute('aria-invalid', 'true'); error(EMPTY_LIST_NAME); name.focus(); return; }
      name.removeAttribute('aria-invalid');
      const latest = store.list(listId);
      if (!latest) return;
      const conflict = (patch.name !== undefined && latest.name !== base.name && latest.name !== patch.name) || (patch.kind !== undefined && latest.kind !== base.kind && latest.kind !== patch.kind);
      const losesDraft = patch.kind !== undefined && shell.detail.dirty();
      const commit = () => {
        if (losesDraft) shell.detail.clear();
        void write(patch);
      };
      if (conflict || losesDraft) {
        popup?.confirm({ title: conflict ? 'Save over newer list changes?' : 'Discard item edits?',
          message: `${conflict ? 'This list changed on another device. Saving will replace the fields you edited.' : ''}${losesDraft ? ' Changing the list type will discard unsaved edits in the open item editor.' : ''}`.trim(),
          keep: { label: 'Keep editing', onChoose: () => popup?.resume() },
          act: { label: 'Save changes', onChoose: commit } });
      } else commit();
    }
    const form = el('form', { class: 'new-list edit-list item-form', attrs: { novalidate: true }, children: [
      el('div', { class: 'form-group', children: [
        el('div', { class: 'field', children: [el('label', { class: 'field-label', text: 'List name', attrs: { for: 'edit-list-name' } }), name] }),
        kind.node] }),
      status, actionRow([cancelButton], saveButton)
    ], on: { submit: event => { event.preventDefault(); save(); } } });
    editPopup = shell.openPopup({ busy: () => saving, title: 'Edit list', body: form, focus: name, returnFocusTo: focus,
      guard: { dirty: () => Object.keys(changes()).length > 0, title: 'Discard list changes?', message: 'Your edits to this list will be lost.', keep: 'Keep editing', discard: 'Discard changes' },
      onClose: () => { popup = undefined; refreshForm = undefined; } });
    popup = editPopup;
    refreshForm = latest => {
      if (saving) return;
      // Pristine fields follow remote changes; touched fields keep their original base.
      if (name.value.trim() === base.name) { name.value = latest.name; base = { ...base, name: latest.name }; }
      if (kind.value() === base.kind) { kind.set(latest.kind); base = { ...base, kind: latest.kind }; }
    };
  }
  // Its own, so a shared list's editor can be offered it alone (g72).
  const clearEntry = (): MenuEntry => ({ label: `Clear completed (${completed().length})`, icon: 'listClear', danger: true, disabled: clearing || completed().length === 0, onSelect: clearCompleted });
  return {
    entries: (): MenuItem[] => [
      { label: 'Edit list', icon: 'edit', onSelect: editList },
      { label: 'Share list', icon: 'share', onSelect: options.share },
      // Who changed this list, and when (g110): only while the server lets its history be read.
      ...(store.activityAvailable() ? [{ label: 'Activity', icon: 'history' as const, onSelect: () => openActivity(listId) }] : []),
      SEPARATOR,
      clearEntry(), SEPARATOR,
      { label: 'Delete list', icon: 'trash', danger: true, onSelect: options.deleteList },
    ],
    clearEntry,
    refresh: () => { const list = store.list(listId); if (list) refreshForm?.(list); refreshQuestion?.(); },
    dispose: () => { disposed = true; popup?.close(); },
  };
}
